import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import apiClient from '../../app/services/api-client'
import { navigateToLogin } from '../../app/modules/auth/core/NavigationHelper'
import { setupFcmTokenAfterLogin, requestNotificationPermission } from '../../firebase'
import { stateUpdateTracker } from '../../app/utils/stateUpdateTracker'
import { getStoredAuth, setStoredAuth, clearAllAuthData } from '../../app/utils/authStorage'
import { isUserDisabled } from '../../app/utils/userValidation'


// Types
export interface AuthState {
  token: string | null
  laravel_token: string | null
  user: any | null
  isAuthenticated: boolean
  isLoading: boolean
  isFetchingUser: boolean
  error: string | null
  lastAuthMeCall: number
  splashScreenLoading: boolean
}

interface LoginPayload {
  email: string
  password: string
}

interface LoginResponse {
  token?: string
  laravel_token?: string
  auth_token?: string
  user?: any
  message?: string
  status?: string
  data?: {
    token?: string
    laravel_token?: string
    auth_token?: string
    user?: any
  }
}

// Initial state
const storedAuth = getStoredAuth()
const initialState: AuthState = {
  token: storedAuth?.token || null,
  laravel_token: storedAuth?.laravel_token || null,
  user: null,
  isAuthenticated: !!(storedAuth?.token || storedAuth?.laravel_token),
  isLoading: false,
  isFetchingUser: false,
  error: null,
  lastAuthMeCall: 0,
  splashScreenLoading: true, // Start with splash screen
}

// Async thunks
export const loginUser = createAsyncThunk<
  LoginResponse,
  LoginPayload,
  { rejectValue: string }
>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/auth/login`, credentials)
      return response.data
    } catch (error: any) {
      // Handle 403 errors specifically for deactivated accounts
      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.message || 'Login failed'
        // Check if this is a deactivated account error
        if (errorMessage.toLowerCase().includes('deactivated') || 
            errorMessage.toLowerCase().includes('disabled') || 
            errorMessage.toLowerCase().includes('inactive')) {
          return rejectWithValue('Account has been deactivated. Please contact support.')
        }
        return rejectWithValue(errorMessage)
      }
      return rejectWithValue(error.response?.data?.message || 'Login failed')
    }
  }
)

export const fetchUserData = createAsyncThunk<
  any,
  { skipDebounce?: boolean } | void,
  { rejectValue: string; state: { auth: AuthState } }
>(
  'auth/fetchUserData',
  async (options = {}, { getState, rejectWithValue }) => {
    try {
      // Use state tracker to prevent excessive updates that cause refresh loops
      if (!(options && options.skipDebounce) && !stateUpdateTracker.shouldAllowUpdate('fetchUserData', 10000)) {
        return rejectWithValue('State update blocked - preventing refresh loop')
      }

      const state = getState()
      const { token, laravel_token, lastAuthMeCall } = state.auth
      
      // Use Laravel token if available, otherwise fall back to regular token
      const tokenToUse = laravel_token ?? token
      if (!tokenToUse) {
        return rejectWithValue('No token available')
      }
      
      // Debounce /auth/me calls - 30 seconds for interceptor calls, but allow immediate calls for manual refresh
      const now = Date.now()
      const timeSinceLastCall = now - lastAuthMeCall
      
      // Allow immediate calls if explicitly requested or if enough time has passed
      if (!(options && options.skipDebounce) && timeSinceLastCall < 5000) {
        return rejectWithValue('Too many requests - preventing excessive API calls')
      }
      
      const response = await apiClient.get(`/auth/me`, {
        headers: {
          'auth-token': tokenToUse,
          'Authorization': `Bearer ${tokenToUse}`,
          'Accept': 'application/json',
        }
      })
      
      const userData = response.data?.data?.user || response.data?.data || response.data?.user || response.data
      
      // Check if user is disabled
      if (isUserDisabled(userData)) {
        // Force logout and navigate to login
        setTimeout(() => {
          navigateToLogin()
        }, 100)
        return rejectWithValue('User account has been disabled')
      }
      
      return userData
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user data')
    }
  }
)

export const logoutUser = createAsyncThunk<
  void,
  void,
  { rejectValue: string; state: { auth: AuthState } }
>(
  'auth/logout',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const { token, laravel_token } = state.auth
      const tokenToUse = laravel_token ?? token
      
      if (tokenToUse) {
        // Get current FCM token to send with logout request
        let fcmToken = null
        try {
          fcmToken = await requestNotificationPermission()
        } catch (fcmError) {
          console.warn('Failed to get FCM token for logout:', fcmError)
        }

        // Prepare logout request body
        const logoutBody: any = {}
        if (fcmToken) {
          logoutBody.fcm_token = fcmToken
        }

        await apiClient.post(`/auth/logout`, logoutBody, {
          headers: {
            'auth-token': tokenToUse,
            'Authorization': `Bearer ${tokenToUse}`,
            'Accept': 'application/json',
          }
        })
      }
    } catch (error: any) {
      // Even if logout API fails, we'll still clear local state
      console.warn('Logout API failed, but clearing local state:', error)
    }
  }
)

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<any>) => {
      state.user = action.payload
    },
    clearAuth: (state) => {
      state.token = null
      state.laravel_token = null
      state.user = null
      state.isAuthenticated = false
      state.error = null
      clearAllAuthData()
    },
    updateLastAuthMeCall: (state) => {
      state.lastAuthMeCall = Date.now()
    },
    setSplashScreenLoading: (state, action: PayloadAction<boolean>) => {
      state.splashScreenLoading = action.payload
    },
    forceLogout: (state) => {
      state.token = null
      state.laravel_token = null
      state.user = null
      state.isAuthenticated = false
      state.error = null
      state.splashScreenLoading = false
      clearAllAuthData()
    }
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        
        // Handle the backend response structure: { status: "Success", data: { token, user } }
        const responseData = action.payload.data || action.payload
        const { token, laravel_token, auth_token, user } = responseData
        
        // Handle different token formats
        state.token = token || auth_token || null
        state.laravel_token = laravel_token || null
        
        if (state.token || state.laravel_token) {
          state.isAuthenticated = true
          // Store auth data (without user data)
          setStoredAuth({
            token: state.token || undefined,
            laravel_token: state.laravel_token || undefined,
          })
        }
        
        // Set user data if provided and check if user is disabled
        if (user) {
          if (isUserDisabled(user)) {
            // User is disabled, force logout
            state.token = null
            state.laravel_token = null
            state.user = null
            state.isAuthenticated = false
            state.error = 'User account has been disabled'
            clearAllAuthData()
            setTimeout(() => {
              navigateToLogin()
            }, 100)
          } else {
            state.user = user
            // Setup FCM token after successful login
            const tokenToUse = state.laravel_token || state.token
            if (tokenToUse && user.id) {
              setupFcmTokenAfterLogin(tokenToUse, user.id).catch(error => {
                console.error('Failed to setup FCM token after login:', error)
              })
            }
          }
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Login failed'
      })
      
      // Fetch User Data
      .addCase(fetchUserData.pending, (state) => {
        state.isFetchingUser = true
        state.error = null
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.isFetchingUser = false
        state.user = action.payload
        state.lastAuthMeCall = Date.now()
        state.splashScreenLoading = false
      })
      .addCase(fetchUserData.rejected, (state, action) => {
        state.isFetchingUser = false
        state.error = action.payload || 'Failed to fetch user data'
        state.splashScreenLoading = false
        
        // If user is disabled or token is invalid, force logout
        if (action.payload === 'User account has been disabled' || 
            action.payload?.includes('Unauthorized') || 
            action.payload?.includes('invalid')) {
          state.token = null
          state.laravel_token = null
          state.user = null
          state.isAuthenticated = false
          clearAllAuthData()
        }
      })
      
      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false
        state.token = null
        state.laravel_token = null
        state.user = null
        state.isAuthenticated = false
        state.error = null
        state.splashScreenLoading = false
        clearAllAuthData()
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false
        // Still clear auth state even if logout API fails
        state.token = null
        state.laravel_token = null
        state.user = null
        state.isAuthenticated = false
        state.error = null
        state.splashScreenLoading = false
        clearAllAuthData()
      })
  },
})

export const { 
  clearError, 
  setUser, 
  clearAuth, 
  updateLastAuthMeCall, 
  setSplashScreenLoading,
  forceLogout 
} = authSlice.actions

export default authSlice.reducer
